/**
 * DoozyGo Sound Effects — Web Audio API
 * 100% royalty-free, programmatically generated.
 * 10 celebration sound options for Magic reveal.
 */

let audioCtx = null;

const getCtx = () => {
  if (!audioCtx) audioCtx = new (window.AudioContext || window.webkitAudioContext)();
  if (audioCtx.state === "suspended") audioCtx.resume();
  return audioCtx;
};

export const warmUpAudio = () => {
  try { getCtx(); } catch (e) { console.debug('[Sound] Audio warmup unavailable:', e.message); }
};

const playTone = (freq, startTime, duration, type = "sine", vol = 0.15) => {
  const ctx = getCtx();
  const osc = ctx.createOscillator();
  const gain = ctx.createGain();
  osc.type = type;
  osc.frequency.setValueAtTime(freq, startTime);
  gain.gain.setValueAtTime(vol, startTime);
  gain.gain.exponentialRampToValueAtTime(0.001, startTime + duration);
  osc.connect(gain);
  gain.connect(ctx.destination);
  osc.start(startTime);
  osc.stop(startTime + duration);
};

const logAudioErr = (name, e) => console.debug(`[Sound] ${name} failed:`, e.message);

// ═══════ 10 SOUND OPTIONS ═══════

/** 1. Sparkle Chime — ascending C major with shimmer */
export const sound1_sparkleChime = () => {
  try {
    const ctx = getCtx(); const now = ctx.currentTime;
    [523, 659, 784, 1047].forEach((f, i) => {
      playTone(f, now + i * 0.12, 0.4, "sine", 0.12);
      playTone(f * 2, now + i * 0.12 + 0.03, 0.25, "sine", 0.05);
    });
    [1319, 1568, 2093].forEach((f, i) => playTone(f, now + 0.5 + i * 0.05, 0.3, "sine", 0.04));
  } catch (e) { logAudioErr('sparkleChime', e); }
};

/** 2. Party Fanfare — triumphant brass arpeggio */
export const sound2_partyFanfare = () => {
  try {
    const ctx = getCtx(); const now = ctx.currentTime;
    [392, 523, 659, 784].forEach((f, i) => playTone(f, now + i * 0.1, 0.5, "triangle", 0.15));
    const horn = ctx.createOscillator(); const hg = ctx.createGain();
    horn.type = "sawtooth"; horn.frequency.setValueAtTime(349, now + 0.45);
    horn.frequency.linearRampToValueAtTime(523, now + 0.55);
    hg.gain.setValueAtTime(0.06, now + 0.45); hg.gain.exponentialRampToValueAtTime(0.001, now + 0.85);
    horn.connect(hg); hg.connect(ctx.destination); horn.start(now + 0.45); horn.stop(now + 0.85);
    [523, 659, 784].forEach(f => playTone(f, now + 0.7, 0.8, "sine", 0.08));
  } catch (e) { logAudioErr('partyFanfare', e); }
};

/** 3. Soft Bell — gentle wind chime */
export const sound3_softBell = () => {
  try {
    const ctx = getCtx(); const now = ctx.currentTime;
    [880, 1109, 1319, 1760].forEach((f, i) => playTone(f, now + i * 0.2, 0.6, "sine", 0.08));
  } catch (e) { logAudioErr('softBell', e); }
};

/** 4. Pop Burst — bubbly pop sounds */
export const sound4_popBurst = () => {
  try {
    const ctx = getCtx(); const now = ctx.currentTime;
    [300, 500, 700, 900, 1200].forEach((f, i) => {
      playTone(f, now + i * 0.07, 0.12, "square", 0.06);
      playTone(f * 1.5, now + i * 0.07 + 0.02, 0.08, "sine", 0.04);
    });
  } catch (e) { logAudioErr('popBurst', e); }
};

/** 5. Harp Glissando — sweeping harp upward */
export const sound5_harpGliss = () => {
  try {
    const ctx = getCtx(); const now = ctx.currentTime;
    [262, 330, 392, 523, 659, 784, 988, 1047].forEach((f, i) => {
      playTone(f, now + i * 0.08, 0.5, "sine", 0.1);
    });
  } catch (e) { logAudioErr('harpGliss', e); }
};

/** 6. Drum Roll + Crash — anticipation then reveal */
export const sound6_drumRoll = () => {
  try {
    const ctx = getCtx(); const now = ctx.currentTime;
    for (let i = 0; i < 8; i++) {
      playTone(150 + i * 10, now + i * 0.05, 0.08, "triangle", 0.08 + i * 0.01);
    }
    // Crash/shimmer
    playTone(200, now + 0.45, 0.6, "sawtooth", 0.04);
    [4000, 5000, 6000].forEach(f => playTone(f, now + 0.45, 0.4, "sine", 0.02));
    [523, 784, 1047].forEach(f => playTone(f, now + 0.5, 0.8, "sine", 0.1));
  } catch (e) { logAudioErr('drumRoll', e); }
};

/** 7. Music Box — delicate melody */
export const sound7_musicBox = () => {
  try {
    const ctx = getCtx(); const now = ctx.currentTime;
    const melody = [784, 880, 784, 659, 784, 1047, 988, 784];
    melody.forEach((f, i) => playTone(f, now + i * 0.15, 0.35, "sine", 0.1));
  } catch (e) { logAudioErr('musicBox', e); }
};

/** 8. Whoosh + Ding — fast reveal with a final ding */
export const sound8_whooshDing = () => {
  try {
    const ctx = getCtx(); const now = ctx.currentTime;
    // Whoosh (rising noise-like)
    for (let i = 0; i < 10; i++) {
      playTone(200 + i * 150, now + i * 0.03, 0.1, "sawtooth", 0.02);
    }
    // Ding
    playTone(1568, now + 0.35, 0.8, "sine", 0.15);
    playTone(2349, now + 0.38, 0.6, "sine", 0.06);
  } catch (e) { logAudioErr('whooshDing', e); }
};

/** 9. Celebration Horns — double horn blast */
export const sound9_horns = () => {
  try {
    const ctx = getCtx(); const now = ctx.currentTime;
    // First horn
    [349, 440, 523].forEach((f, i) => playTone(f, now + i * 0.05, 0.3, "sawtooth", 0.05));
    // Second horn (higher)
    [523, 659, 784].forEach((f, i) => playTone(f, now + 0.35 + i * 0.05, 0.4, "sawtooth", 0.05));
    // Sustain chord
    [523, 659, 784].forEach(f => playTone(f, now + 0.6, 1.0, "sine", 0.07));
  } catch (e) { logAudioErr('horns', e); }
};

/** 10. Twinkle Stars — ethereal fairy dust */
export const sound10_twinkle = () => {
  try {
    const ctx = getCtx(); const now = ctx.currentTime;
    const notes = [1047, 1319, 1568, 1760, 2093, 1760, 2093, 2637];
    notes.forEach((f, i) => {
      playTone(f, now + i * 0.1, 0.3, "sine", 0.08);
      playTone(f * 0.5, now + i * 0.1 + 0.05, 0.2, "sine", 0.03);
    });
  } catch (e) { logAudioErr('twinkle', e); }
};

// ═══════ NAMED LIST FOR UI ═══════
export const SOUND_OPTIONS = [
  { id: "sparkle", name: "Sparkle Chime", play: sound1_sparkleChime },
  { id: "fanfare", name: "Party Fanfare", play: sound2_partyFanfare },
  { id: "bell", name: "Soft Bell", play: sound3_softBell },
  { id: "pop", name: "Pop Burst", play: sound4_popBurst },
  { id: "harp", name: "Harp Glissando", play: sound5_harpGliss },
  { id: "drum", name: "Drum Roll", play: sound6_drumRoll },
  { id: "musicbox", name: "Music Box", play: sound7_musicBox },
  { id: "whoosh", name: "Whoosh + Ding", play: sound8_whooshDing },
  { id: "horns", name: "Celebration Horns", play: sound9_horns },
  { id: "twinkle", name: "Twinkle Stars", play: sound10_twinkle },
];

/** Get the active sound function — always Party Fanfare */
export const getActiveSound = () => sound2_partyFanfare;

/** Play the celebration sound (Party Fanfare everywhere) */
export const playMagicReveal = () => { sound2_partyFanfare(); };

/** Booking confirmed — Party Fanfare */
export const playBookingConfirmed = () => { sound2_partyFanfare(); };
